import { Mail, MapPin, CalendarCheck2, MessageCircle, QrCode, Copy, ExternalLink } from "lucide-react";
import { RevealSection } from "@/components/common/RevealSection";
import { LeadForm } from "@/components/forms/LeadForm";
import { getWhatsAppLink, getWhatsAppWebLink, siteConfig } from "@/content/siteData";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ContactPage() {
  const whatsappLink = getWhatsAppLink();
  const whatsappWebLink = getWhatsAppWebLink();
  const whatsappQrLink = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(whatsappWebLink)}`;

  const copyWhatsAppNumber = async () => {
    try {
      await navigator.clipboard.writeText(siteConfig.whatsappDisplayNumber);
      toast.success("WhatsApp number copied");
    } catch (error) {
      toast.error("Could not copy number. Please try again.");
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24" data-testid="contact-page">
      <RevealSection className="mx-auto grid w-full max-w-7xl gap-8 py-14 md:grid-cols-[0.92fr_1.08fr] md:py-20">
        <div className="rounded-2xl border border-border bg-white p-8 soft-card">
          <p className="text-base text-primary md:text-lg" data-testid="contact-label">
            Free trial booking
          </p>
          <h1 className="mt-3 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="contact-heading">
            Book your personalized trial class.
          </h1>
          <p className="mt-4 text-sm text-muted-foreground md:text-base" data-testid="contact-description">
            Share your goals and current comfort level. Prabha will design your personalized spoken Hindi learning path
            for daily life, diplomatic settings, or family integration in India.
          </p>

          <div className="mt-6 space-y-4">
            <p className="flex items-center gap-2 text-sm text-foreground/90 md:text-base" data-testid="contact-email-info">
              <Mail className="h-4 w-4 text-primary" />
              {siteConfig.email}
            </p>
            <p className="flex items-center gap-2 text-sm text-foreground/90 md:text-base" data-testid="contact-city-info">
              <MapPin className="h-4 w-4 text-primary" />
              {siteConfig.city}
            </p>
            <p className="flex items-center gap-2 text-sm text-foreground/90 md:text-base" data-testid="contact-trial-feature">
              <CalendarCheck2 className="h-4 w-4 text-primary" />
              No commitment, just Hindi that works in real life.
            </p>
            <p className="text-sm text-foreground/90" data-testid="contact-call-hours">
              Direct call support: {siteConfig.callHours}
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="contact-primary-action-buttons">
              <Button
                asChild
                variant="outline"
                className="h-10 w-full justify-center rounded-full border-primary px-5 text-sm text-primary hover:bg-primary hover:text-white"
                data-testid="contact-call-now-button"
              >
                <a href={`tel:+${siteConfig.whatsappNumber}`}>Call now</a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-10 w-full justify-center rounded-full border-primary px-5 text-sm text-primary hover:bg-primary hover:text-white"
                data-testid="contact-whatsapp-chat-button"
              >
                <a href={whatsappLink}>
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp chat
                </a>
              </Button>
            </div>

            <div className="rounded-2xl border border-border bg-muted/35 p-4" data-testid="contact-whatsapp-panel">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary/80" data-testid="contact-whatsapp-panel-heading">
                WhatsApp support options
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Button
                  asChild
                  variant="ghost"
                  className="h-10 justify-start rounded-xl border border-border bg-white px-3 text-sm"
                  data-testid="contact-whatsapp-open-tab-button"
                >
                  <a href={whatsappWebLink} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    Open in new tab
                  </a>
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={copyWhatsAppNumber}
                  className="h-10 justify-start rounded-xl border border-border bg-white px-3 text-sm"
                  data-testid="contact-copy-whatsapp-number-button"
                >
                  <Copy className="h-4 w-4" />
                  Copy WhatsApp number
                </Button>
              </div>

              <div className="mt-4 flex items-center gap-4 rounded-xl border border-border bg-white p-3" data-testid="contact-whatsapp-qr-section">
                <img
                  src={whatsappQrLink}
                  alt="QR code to start WhatsApp chat"
                  className="h-20 w-20 rounded-md border border-border bg-white p-1"
                  data-testid="contact-whatsapp-qr-image"
                />
                <div>
                  <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-primary/80" data-testid="contact-whatsapp-qr-heading">
                    <QrCode className="h-3.5 w-3.5" />
                    Scan QR to chat
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground" data-testid="contact-whatsapp-qr-note">
                    If browser links fail, scan this QR from your phone camera to start WhatsApp chat.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-muted/70 p-8 soft-card">
          <LeadForm formType="trial" submitLabel="Book My Free Trial Now" formId="inquiry-form" />
        </div>
      </RevealSection>
    </div>
  );
}